/*
  Fetches related-product data from the invisible "Complete the set (data)"
  section (the only way to reach Shopify's recommendation engine, which is
  section-scoped), then renders the UI directly inside this block - no
  morphSection/section replacement involved, since this needs to stay a
  normal nested block wherever it's placed inside Product information.

  Uses the "related" intent, not "complementary": complementary
  recommendations only exist once Shopify's own algorithm has enough
  order history to generate them, and this store doesn't have any yet -
  every product query came back empty. "related" is populated for every
  product from day one (metafields/tags/vector similarity), so this is
  what actually renders instead of silently showing nothing forever.
*/
class CompleteTheSet extends HTMLElement {
  connectedCallback() {
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        this.load();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  async load() {
    const { url } = this.dataset;
    if (!url) return;

    try {
      const response = await fetch(url);
      if (!response.ok) return;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const script = doc.querySelector('[data-complete-the-set-json]');
      if (!script) return;

      const products = JSON.parse(script.textContent);
      if (!Array.isArray(products) || products.length === 0) return;

      this.render(products);
    } catch (error) {
      console.error('Complete the set error:', error);
    }
  }

  render(products) {
    const mainVariantId = Number(this.dataset.mainVariantId);
    const mainPrice = Number(this.dataset.mainPrice);
    const heading = this.dataset.heading || 'Complete The Set';
    const buttonLabel = this.dataset.buttonLabel || 'Add Set';

    const itemsHtml = products
      .map(
        (product, index) => `
          <div class="complete-the-set__item is-selected" data-index="${index}" data-variant-id="${product.variantId}" data-price="${product.price}">
            <a href="${product.url}" class="complete-the-set__image">
              <img src="${product.image}" alt="${this.escapeHtml(product.title)}" loading="lazy" width="160" height="160">
            </a>
            <div class="complete-the-set__info">
              <a href="${product.url}" class="complete-the-set__title">${this.escapeHtml(product.title)}</a>
              <span class="complete-the-set__price">${product.priceFormatted}</span>
            </div>
            <button type="button" class="complete-the-set__toggle" aria-pressed="true" aria-label="Remove from set - ${this.escapeHtml(product.title)}">
              <span class="complete-the-set__toggle-icon" aria-hidden="true"></span>
            </button>
          </div>
        `
      )
      .join('');

    this.innerHTML = `
      <div class="complete-the-set__inner">
        <div class="complete-the-set__header">
          <span class="complete-the-set__heading">${this.escapeHtml(heading)}</span>
        </div>
        <div class="complete-the-set__list">${itemsHtml}</div>
        <button type="button" class="complete-the-set__add-button">
          <span class="complete-the-set__add-label">${this.escapeHtml(buttonLabel)}</span>
          <span class="complete-the-set__add-divider" aria-hidden="true">&middot;</span>
          <span class="complete-the-set__add-price"></span>
        </button>
      </div>
    `;

    this.wireInteractions(mainVariantId, mainPrice);
  }

  escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }

  wireInteractions(mainVariantId, mainPrice) {
    const items = Array.from(this.querySelectorAll('.complete-the-set__item'));
    const addButton = this.querySelector('.complete-the-set__add-button');
    const priceLabel = this.querySelector('.complete-the-set__add-price');
    if (!addButton || !priceLabel) return;

    const selectedItems = () => items.filter((item) => item.classList.contains('is-selected'));

    const formatMoney = (cents) =>
      (cents / 100).toLocaleString('en-IN', {
        style: 'currency',
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'INR',
        maximumFractionDigits: 0,
      });

    const updateTotal = () => {
      const total = mainPrice + selectedItems().reduce((sum, item) => sum + Number(item.dataset.price || 0), 0);
      priceLabel.textContent = formatMoney(total);
    };

    items.forEach((item) => {
      const toggle = item.querySelector('.complete-the-set__toggle');
      if (!toggle) return;
      toggle.addEventListener('click', () => {
        const nowSelected = !item.classList.contains('is-selected');
        item.classList.toggle('is-selected', nowSelected);
        item.classList.toggle('is-removed', !nowSelected);
        toggle.setAttribute('aria-pressed', String(nowSelected));
        updateTotal();
      });
    });

    addButton.addEventListener('click', async () => {
      if (!mainVariantId) return;
      const lineItems = [{ id: mainVariantId, quantity: 1 }].concat(
        selectedItems().map((item) => ({ id: Number(item.dataset.variantId), quantity: 1 }))
      );

      addButton.dataset.loading = 'true';
      addButton.disabled = true;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: lineItems }),
        });

        if (!response.ok) throw new Error('Add set request failed');

        window.location.reload();
      } catch (error) {
        addButton.dataset.loading = 'false';
        addButton.disabled = false;
        console.error(error);
      }
    });

    updateTotal();
  }
}

if (!customElements.get('complete-the-set-component')) {
  customElements.define('complete-the-set-component', CompleteTheSet);
}
